import { ArrowRight, Layers, MessageSquareOff, Rocket, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const PILLARS = [
    {
        icon: Layers,
        title: 'One workspace, not five subscriptions',
        description:
            'Chat widget, guided-question builder, CRM, appointment requests, and pricing intelligence all read and write to the same tenant data — a lead captured in a conversation is already a lead in your pipeline, not a CSV you export and re-import somewhere else.',
    },
    {
        icon: MessageSquareOff,
        title: 'You control what the assistant says',
        description:
            'Guided Questions let you define the exact conversation tree — what’s asked, in what order, and what happens on each answer. Turn on an AI provider for open-ended follow-up questions, or set the assistant to Custom mode so it only ever asks from the script you wrote — never off-topic, never improvised.',
    },
    {
        icon: ShieldCheck,
        title: 'Bring your own AI provider, or none at all',
        description:
            'Connect OpenAI, Anthropic, or Gemini with your own API key for free-text conversation, switch providers at any time, or run guided-questions-only with no AI provider connected. It’s your choice per assistant, not a bundled black box.',
    },
    {
        icon: Rocket,
        title: 'Live in one script tag',
        description:
            'Publish an assistant, set your logo and colors, and paste one script tag into your site. No SDK to install, no build step on your end — the same widget you can try right on this page.',
    },
];

export function WhyAipaPage() {
    const { user, isAuthenticated, isLoading } = useAuth();
    const dashboardHref = user?.is_platform_admin ? '/admin' : '/dashboard';

    usePageSeo({
        title: 'Why AIPA — The AI Platform Built to Convert Visitors Into Leads',
        description:
            'See why growing teams choose AIPA over a generic chatbot: an AI assistant, pricing intelligence, and CRM working together to turn website traffic into booked appointments.',
        keywords:
            'why choose AIPA, best AI chatbot for lead conversion, AI lead qualification software, pricing intelligence benefits, AI powered sales platform',
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="mx-auto max-w-3xl px-6 py-20 text-center">
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Why teams choose AIPA</h1>
                <p className="mt-6 text-lg text-muted-foreground">
                    Most teams get here after outgrowing a stack of separate tools — a chatbot vendor, a spreadsheet of leads, a scheduling
                    link, and a pricing model nobody fully trusts. AIPA replaces that stack with one workspace built around a single idea:
                    every visitor conversation should turn into a qualified, trackable lead with as little manual work as possible.
                </p>
            </section>

            <section className="border-t bg-muted/30 py-16">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 sm:grid-cols-2">
                    {PILLARS.map((pillar) => (
                        <div key={pillar.title} className="rounded-lg border bg-card p-6 shadow-sm">
                            <div className="flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground">
                                <pillar.icon className="size-6" />
                            </div>
                            <h3 className="mt-4 font-medium">{pillar.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground">{pillar.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-16">
                <div className="mx-auto max-w-4xl px-6">
                    <h2 className="text-center text-2xl font-semibold tracking-tight">The stack this replaces</h2>
                    <div className="mt-10 overflow-hidden rounded-xl border">
                        <div className="grid grid-cols-2 divide-x text-sm">
                            <div className="bg-muted/30 p-5">
                                <p className="mb-4 font-semibold text-muted-foreground">A typical stitched-together stack</p>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li>A chat widget vendor, configured separately</li>
                                    <li>Leads exported and re-imported into a CRM</li>
                                    <li>A scheduling tool with its own login</li>
                                    <li>Pricing decisions made in a spreadsheet</li>
                                    <li>No shared record of what a visitor actually asked</li>
                                </ul>
                            </div>
                            <div className="p-5">
                                <p className="mb-4 font-semibold text-primary">With AIPA</p>
                                <ul className="space-y-3">
                                    <li>Chat, CRM, appointments, and pricing in one workspace</li>
                                    <li>Every conversation is already a lead record</li>
                                    <li>Appointment requests come straight from the chat</li>
                                    <li>Pricing recommendations grounded in your own data</li>
                                    <li>Full conversation history attached to every lead</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t bg-primary text-primary-foreground">
                <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 py-16 text-center">
                    <h2 className="text-2xl font-semibold tracking-tight">See it running on your own workspace</h2>
                    {!isLoading && isAuthenticated ? (
                        <Button size="lg" variant="secondary" asChild>
                            <Link to={dashboardHref}>
                                Go to your dashboard <ArrowRight className="ml-1 size-4" />
                            </Link>
                        </Button>
                    ) : (
                        <Button size="lg" variant="secondary" asChild>
                            <Link to="/company/contact">
                                Talk to our team <ArrowRight className="ml-1 size-4" />
                            </Link>
                        </Button>
                    )}
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
